import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { productsApi } from "@/lib/admin-api/products";

// ---------------------------------------------------------------------------
// LowStockAlertWidget
// Protects: the "at-a-glance" inventory alert surface on the admin dashboard.
// Renders null when there are no alerts to avoid visual noise.
// ---------------------------------------------------------------------------

export function LowStockAlertWidget() {
  const { t } = useTranslation("admin");
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "products", "alerts-count"],
    queryFn: () => productsApi.alertsCount(),
    refetchInterval: 60_000, // Match 60s server cache
  });

  if (isLoading) {
    return (
      <div
        className="animate-pulse rounded-lg border border-stone-200 p-4"
        aria-hidden="true"
      >
        <div className="h-4 w-24 rounded bg-stone-200" />
        <div className="mt-3 h-6 w-16 rounded bg-stone-200" />
      </div>
    );
  }

  if (!data || data.total_alerts === 0) return null;

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4">
      <h3 className="flex items-center gap-2 text-sm font-medium text-stone-700">
        <AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden="true" />
        {t("products.alerts.title", "Alertes de stock")}
      </h3>
      <div className="mt-3 space-y-1">
        {data.out_of_stock > 0 && (
          <p className="flex items-center gap-2 text-sm">
            <span
              className="inline-block h-2 w-2 rounded-full bg-red-500"
              aria-hidden="true"
            />
            <span className="font-medium tabular-nums">{data.out_of_stock}</span>
            <span className="text-stone-600">
              {t("products.alerts.out_of_stock", "en rupture")}
            </span>
          </p>
        )}
        {data.low_stock > 0 && (
          <p className="flex items-center gap-2 text-sm">
            <span
              className="inline-block h-2 w-2 rounded-full bg-amber-500"
              aria-hidden="true"
            />
            <span className="font-medium tabular-nums">{data.low_stock}</span>
            <span className="text-stone-600">
              {t("products.alerts.low_stock", "en stock faible")}
            </span>
          </p>
        )}
      </div>
      <Link
        to="/admin/produits?stock=alerte"
        className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
      >
        {t("products.alerts.view_all", "Voir toutes les alertes")}
        <ArrowRight className="h-3 w-3" aria-hidden="true" />
      </Link>
    </div>
  );
}
